import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    Stack,
    Chip,
    Checkbox,
    FormControlLabel,
    IconButton,
    Divider,
    Dialog,
    DialogContent,
    alpha,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Alert,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    TextField,
    MenuItem,
    LinearProgress,
    Tooltip,
    Autocomplete,
    InputAdornment,
    Pagination,
    Card,
    Collapse,
    FormControl,
    InputLabel,
    Select,
} from '@mui/material';
import { formatToUIDateTimeLong } from '../utils/dateUtils';
import {
    Timeline as TimelineIcon,
    History as HistoryIcon,
    LocalPharmacy as LocalPharmacyIcon,
    Event as EventIcon,
    Description as DescriptionIcon,
    AssignmentInd as AssignmentIndIcon,
    Medication as MedicationIcon,
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    Assignment as AssignmentIcon,
    SwapHoriz as SwapHorizIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    CheckCircle as CheckCircleIcon,
    WarningAmber as WarningAmberIcon,
    MedicalInformation as MedicalInformationIcon,
    Room as RoomIcon,
    MonitorHeart as MonitorHeartIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    PeopleAlt as PeopleAltIcon,
    PriorityHigh as PriorityHighIcon,
    AccessTime as AccessTimeIcon,
    AutoFixHigh as AutoFixHighIcon,
    InfoOutlined as InfoOutlinedIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    StickyNote2 as NoteIcon,
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon,
    SyncAlt as SyncAltIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import careService from '../services/careService';
import residentService from '../services/residentService';
import vitalsService from '../services/vitalsService';
import clinicalNoteService from '../services/clinicalNoteService';
import masterDataService from '../services/masterDataService';
import { useTenant } from '../contexts/TenantContext';
import { ShiftHandoverDTO, ResidentDTO } from '../types/crm.types';
import { useSpeechToText } from '../hooks/useSpeechToText';

// ─── Stat Card ──────────────────────────────────────────────────────────────
interface StatCardProps { label: string; value: string | number; icon: React.ReactNode; color: string; sub?: string; onClick?: () => void; active?: boolean; }
const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color, sub, onClick, active }) => (
    <Card
        elevation={0}
        onClick={onClick}
        sx={{
            p: 2.5,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: active ? color : 'divider',
            background: '#fff',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            transition: 'all 0.2s ease-in-out',
            cursor: onClick ? 'pointer' : 'default',
            '&:hover': {
                boxShadow: onClick ? `0 4px 16px ${alpha(color, 0.15)}` : 'none',
                borderColor: onClick ? alpha(color, 0.5) : 'divider'
            },
            bgcolor: active ? alpha(color, 0.04) : 'white',
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
                sx={{
                    p: 1.25,
                    borderRadius: '12px',
                    background: `${color}20`,
                    color: color,
                    display: 'flex',
                }}
            >
                {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 28 } })}
            </Box>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: color, lineHeight: 1, fontSize: "1.75rem" }}>
                    {value}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: "0.82rem", whiteSpace: 'nowrap' }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontSize: '0.75rem', display: 'block', mt: 0.2, lineHeight: 1 }}>
                        {sub}
                    </Typography>
                )}
            </Box>
        </Box>
    </Card>
);

// ─── ReadOnly Vital Box ──────────────────────────────────────────────────────
interface VitalBoxProps { label: string; value: string; unit: string; color?: string; }
const VitalBox: React.FC<VitalBoxProps> = ({ label, value, unit, color = '#0077B6' }) => (
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(color, 0.06), border: '1px solid', borderColor: alpha(color, 0.2), textAlign: 'center' }}>
        <Typography variant="h6" fontWeight="800" color={color} lineHeight={1}>{value || '—'}</Typography>
        <Typography variant="caption" color="text.secondary" display="block">{unit}</Typography>
        <Typography variant="caption" fontWeight="700" color="text.secondary">{label}</Typography>
    </Box>
);

// ─── Status Color Map ───────────────────────────────────────────────────────
const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    'Stable': { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
    'Needs Attention': { bg: '#fef9c3', text: '#a16207', border: '#fde047' },
    'Sleeping': { bg: '#ede9fe', text: '#6d28d9', border: '#c4b5fd' },
    'Behavioral Alert': { bg: '#ffedd5', text: '#c2410c', border: '#fdba74' },
    'Hospitalized': { bg: '#cffafe', text: '#0e7490', border: '#67e8f9' },
    'Critical': { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
    'Pending': { bg: '#f1f5f9', text: '#64748b', border: '#cbd5e1' },
};

const getStatusStyle = (status: string) =>
    STATUS_COLORS[status] ?? { bg: '#e0f2fe', text: '#0369a1', border: '#7dd3fc' };

// ─── Main Component ─────────────────────────────────────────────────────────
const ShiftHandoverPage: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { currentTenantId } = useTenant();

    const [loading, setLoading] = useState(true);
    const [handovers, setHandovers] = useState<ShiftHandoverDTO[]>([]);
    const [residents, setResidents] = useState<ResidentDTO[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedHandover, setSelectedHandover] = useState<ShiftHandoverDTO | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(15);
    const [listeningNotes, setListeningNotes] = useState(false);
    const [activeFilter, setActiveFilter] = useState<'ALL' | 'TODAY' | 'CRITICAL'>('ALL');
    const [historyPage, setHistoryPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState<ShiftHandoverDTO[] | null>(null);
    const [statusOptions, setStatusOptions] = useState<any[]>([]);
    const [filterStatus, setFilterStatus] = useState<string>('');

    const defaultForm = (): Partial<ShiftHandoverDTO> => ({
        residentId: '',
        residentName: '',
        roomNumber: '',
        situation: '',
        background: '',
        assessment: '',
        recommendation: '',
        safetyAlerts: [],
        status: 'Stable',
        isCritical: false,
        mood: false,
        vitals: { bp: '', temp: '', pulse: '', spO2: '' },
        checklist: [
            { task: 'Meds Administered', completed: false },
            { task: 'Chart Updated', completed: false },
            { task: 'ADLs Completed', completed: false },
            { task: 'Get Vitals', completed: false }
        ],
        notes: '',
        taskCategories: [
            {
                key: 'bowel', label: 'Bowel', color: '#0891b2',
                tasks: [
                    { id: 'bw-check', label: 'Bowel Movement', completed: false },
                    { id: 'ur-check', label: 'Urinary', completed: false },
                    { id: 'cd-check', label: 'Catheter/Drain', completed: false },
                    { id: 'ic-check', label: 'Incontinence Care', completed: false }
                ]
            },
            {
                key: 'bodycare', label: 'Body Care', color: '#059669',
                tasks: [
                    { id: 'bc-bath', label: 'Full Bath', completed: false },
                    { id: 'bc-oral', label: 'Oral Care', completed: false },
                    { id: 'bc-dress', label: 'Assist with Clothing', completed: false },
                    { id: 'bc-shampoo', label: 'Shampoo/Soap', completed: false },
                    { id: 'bc-partial', label: 'Partial Bath', completed: false },
                    { id: 'bc-shave', label: 'Shave', completed: false },
                    { id: 'bc-hair', label: 'Comb Hair', completed: false },
                    { id: 'bc-hand-nail', label: 'Hand Nail Care', completed: false },
                    { id: 'bc-nail', label: 'Toe Nail Care', completed: false }
                ]
            },
            {
                key: 'skincare', label: 'Skin Care', color: '#db2777',
                tasks: [
                    { id: 'sc-barrier', label: 'Apply Skin Barrier', completed: false },
                    { id: 'sc-pedi', label: 'Pedicare', completed: false },
                    { id: 'sc-lower', label: 'Lower Extremities', completed: false },
                    { id: 'sc-lotion', label: 'Apply Lotion', completed: false },
                    { id: 'sc-elevate', label: 'Elevate Leg', completed: false }
                ]
            },
            {
                key: 'meals', label: 'Meals', color: '#d97706',
                tasks: [
                    { id: 'm-br', label: 'Breakfast', completed: false },
                    { id: 'm-lu', label: 'Lunch', completed: false },
                    { id: 'm-di', label: 'Dinner', completed: false },
                    { id: 'o-snack', label: 'Snack/Water', completed: false }
                ]
            },
            {
                key: 'bed', label: 'Bed', color: '#7c3aed',
                tasks: [
                    { id: 'bd-med', label: 'Medication', completed: false },
                    { id: 'bd-mood', label: 'Mood', completed: false },
                    { id: 'bd-check', label: 'Night Checkup', completed: false }
                ]
            }
        ]
    });

    const [formData, setFormData] = useState<Partial<ShiftHandoverDTO>>(defaultForm());
    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
        bowel: true,
        bodycare: true,
        skincare: true,
        meals: true,
        bed: true,
    });
    const notesAtStartRef = useRef('');

    const { isListening, startListening, stopListening, isSupported } = useSpeechToText({
        interimResults: true,
        continuous: true,
        onResult: (spokenText) => {
            const base = (notesAtStartRef.current || "").trim();
            const cleanedSpoken = spokenText.trim();
            const newVal = base
                ? (cleanedSpoken ? `${base} ${cleanedSpoken}` : base)
                : cleanedSpoken;
            setFormData(prev => ({
                ...prev,
                handoverNote: newVal
            }));
        },
        onEnd: () => setListeningNotes(false)
    });

    const handleToggleDictation = () => {
        if (isListening) {
            stopListening();
            setListeningNotes(false);
        } else {
            notesAtStartRef.current = formData.handoverNote || '';
            setListeningNotes(true);
            startListening();
        }
    };

    // Auto-populate loading state
    const [vitalsLoading, setVitalsLoading] = useState(false);
    const [notesLoading, setNotesLoading] = useState(false);
    const [vitalsSource, setVitalsSource] = useState<string | null>(null);
    const [notesSource, setNotesSource] = useState<string | null>(null);

    const [residentSearchQuery, setResidentSearchQuery] = useState('');
    const [residentSearchOpen, setResidentSearchOpen] = useState(false);
    const [residentSearchResults, setResidentSearchResults] = useState<ResidentDTO[]>([]);
    const [selectedResident, setSelectedResident] = useState<ResidentDTO | null>(null);
    const [isSearchingResidents, setIsSearchingResidents] = useState(false);
    const [activityTimelineData, setActivityTimelineData] = useState<any[]>([]);
    const [isTimelineLoading, setIsTimelineLoading] = useState(false);
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
    const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const apiSearchDebounceRef = useRef<NodeJS.Timeout | null>(null);


    useEffect(() => { fetchData(); }, [currentTenantId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [handoverData, residentData, statusData, statsData] = await Promise.all([
                careService.getHandovers(currentTenantId, 0, 1000),
                currentTenantId ? residentService.getActiveLookup(currentTenantId) : careService.getCustomers(currentTenantId),
                masterDataService.getActiveLookupValuesByName('ShiftHandoverStatus').catch(() => []),
                careService.getHandoverStats(currentTenantId || '').catch(() => null)
            ]);
            const sortedResidents = [...residentData].sort((a, b) => {
                const nameA = (a.fullName || a.firstName || '').toLowerCase();
                const nameB = (b.fullName || b.firstName || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
            setHandovers(handoverData);
            setResidents(sortedResidents);
            setResidentSearchResults(sortedResidents.slice(0, 25));
            setStatusOptions(statusData || []);
            setStats(statsData);
        } catch (error) {
            console.error('Error loading shift handover data', error);
            showSnackbar('Error loading data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => setSnackbar({ open: true, message, severity });

    // ── Search Logic ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!searchTerm.trim() && !filterStatus) {
            setSearchResults(null);
            setSearchLoading(false);
            return;
        }

        if (apiSearchDebounceRef.current) clearTimeout(apiSearchDebounceRef.current);

        // Immediately clear previous API results so local filtering takes over instantly for a snappy UI
        setSearchResults(null);
        setSearchLoading(true);

        apiSearchDebounceRef.current = setTimeout(async () => {
            try {
                const results = await careService.searchHandovers(
                    currentTenantId || '',
                    searchTerm,
                    filterStatus
                );
                setSearchResults(results);
            } catch (error) {
                console.error("Search failed", error);
            } finally {
                setSearchLoading(false);
            }
        }, 500);

        return () => {
            if (apiSearchDebounceRef.current) clearTimeout(apiSearchDebounceRef.current);
        };
    }, [searchTerm, filterStatus, currentTenantId]);

    // ── Auto-populate vitals when resident selected ──────────────────────────
    const autoFetchVitals = useCallback(async (residentId: string) => {
        if (!residentId) return;
        setVitalsLoading(true);
        setVitalsSource(null);
        try {
            let latest: any;
            try {
                // Try the dedicated latest vitals endpoint
                latest = await vitalsService.getLatestVitals(residentId);
            } catch (err) {
                console.warn('Latest vitals API failed, falling back to history:', err);
                // Fallback: get history and pick the most recent one
                const records = await vitalsService.getVitals(residentId);
                const sorted = [...records].sort((a, b) =>
                    new Date(b.vitalDate || b.createdAt || 0).getTime() -
                    new Date(a.vitalDate || a.createdAt || 0).getTime(),
                );
                latest = sorted[0];
            }

            if (latest) {
                const bp = latest.dailyBloodPressure || latest.bloodPressure || '';
                const temp = latest.temperature || '';
                const pulse = latest.pulseRate || '';
                const spo2Value = latest.spO2 || latest.oxygenSaturation || '';

                setFormData(prev => ({
                    ...prev,
                    vitals: {
                        bp: bp,
                        temp: temp,
                        pulse: pulse,
                        spO2: spo2Value,
                    },
                    // Also populate top-level fields for backend compatibility
                    dailyBloodPressure: bp,
                    temperature: temp,
                    pulseRate: pulse,
                    spO2: spo2Value,
                    checklist: prev.checklist?.map(c => c.task === 'Get Vitals' ? { ...c, completed: true } : c) || prev.checklist
                }));

                const dateStr = latest.vitalDate ? new Date(latest.vitalDate).toLocaleString() : 'recent record';
                setVitalsSource(`Auto-filled from latest record (${dateStr})`);
            } else {
                setVitalsSource('No vitals records found for this resident.');
            }
        } catch (error) {
            console.error('Error auto-fetching vitals:', error);
            setVitalsSource('Failed to auto-fetch vitals.');
        } finally {
            setVitalsLoading(false);
        }
    }, []);

    // ── Fetch Handover History (Simplified for Audit Trail) ──────────────────
    const fetchActivityTimeline = useCallback(async (residentId: string) => {
        // No longer fetching all handovers, as Recent Updates serves as the current record's audit trail.
    }, []);

    const getActivityIcon = (type: string) => <AssignmentIndIcon fontSize="small" />;
    const getActivityColor = (type: string) => "#0077B6";

    const renderActivityTimeline = () => {
        // Sort history by updatedAt descending (latest first)
        const history = [...(formData.history || [])].sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );

        if (history.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                        No update history found.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        left: 17,
                        top: 0,
                        bottom: 0,
                        width: '2px',
                        bgcolor: 'grey.100',
                        zIndex: 0
                    }}
                />

                <Stack spacing={2} sx={{ position: 'relative', zIndex: 1, pr: 1 }}>
                    {history.map((activity, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'white',
                                    border: '2px solid',
                                    borderColor: '#0077B6',
                                    flexShrink: 0,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                    zIndex: 2
                                }}
                            >
                                <HistoryIcon fontSize="small" sx={{ color: '#0077B6' }} />
                            </Box>

                            <Card
                                variant="outlined"
                                sx={{
                                    flexGrow: 1,
                                    borderLeft: '4px solid',
                                    borderLeftColor: '#0077B6',
                                    borderRadius: 1.5,
                                    bgcolor: 'white'
                                }}
                            >
                                <Box sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography variant="caption" fontWeight="800" sx={{ lineHeight: 1.2, color: 'text.primary', display: 'block' }}>
                                            Record Updated by {activity.userName || 'Staff Member'}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                        <AccessTimeIcon sx={{ fontSize: 12 }} />
                                        {formatToUIDateTimeLong(activity.updatedAt)}
                                    </Typography>
                                </Box>
                            </Card>
                        </Box>
                    ))}
                </Stack>
            </Box>
        );
    };

    const renderSBARForm = () => (
        <Box sx={{ p: 2.5, bgcolor: 'white', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <MedicalInformationIcon sx={{ color: '#0077B6', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="800" color="text.primary">SBAR Clinical Summary</Typography>
                </Stack>
                {notesLoading ? <CircularProgress size={16} /> : notesSource && (
                    <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AutoFixHighIcon sx={{ fontSize: 14 }} /> From Progress Note
                    </Typography>
                )}
            </Stack>
            <Grid container spacing={2}>
                {[
                    { key: 'situation', label: 'S — Situation', placeholder: 'Enter situation details...' },
                    { key: 'background', label: 'B — Background', placeholder: 'Test background' },
                    { key: 'assessment', label: 'A — Assessment', placeholder: 'Test Assessment' },
                    { key: 'recommendation', label: 'R — Recommendation', placeholder: 'Test' },
                ].map(({ key, label, placeholder }) => (
                    <Grid item xs={12} key={key}>
                        <TextField
                            fullWidth multiline rows={2}
                            required={key === 'situation'}
                            label={label} size="small" placeholder={placeholder}
                            value={(formData as any)[key] || ''}
                            onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' },
                                '& .MuiFormLabel-asterisk': { color: 'error.main' }
                            }}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    const renderVitalsSection = () => (
        <Box sx={{ p: 2.5, bgcolor: 'white', borderRadius: 3, border: '1px solid', borderColor: formData.isCritical ? alpha('#DC2626', 0.4) : 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <MonitorHeartIcon sx={{ color: '#DC2626', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="800" color="text.primary">Latest Vitals</Typography>
                </Stack>
                {vitalsLoading ? <CircularProgress size={16} /> : (formData.vitals?.bp || formData.vitals?.temp) && (
                    <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700 }}></Typography>
                )}
            </Stack>
            <Grid container spacing={1.5}>
                <Grid item xs={3}><VitalBox label="BP" value={formData.vitals?.bp || ''} unit="mmHg" /></Grid>
                <Grid item xs={3}><VitalBox label="SpO₂" value={formData.vitals?.spO2 || ''} unit="%" color="#059669" /></Grid>
                <Grid item xs={3}><VitalBox label="Pulse" value={formData.vitals?.pulse || ''} unit="BPM" color="#d97706" /></Grid>
                <Grid item xs={3}><VitalBox label="Temp" value={formData.vitals?.temp || ''} unit="°F" color="#7C3AED" /></Grid>
            </Grid>
        </Box>
    );

    const renderChecklistSection = () => (
        <Box sx={{ p: 2.5, bgcolor: 'white', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <AssignmentTurnedInIcon sx={{ color: '#7C3AED', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight="800" color="text.primary">Handover Checklist</Typography>
            </Stack>
            <Grid container spacing={1}>
                {formData.checklist?.map((item, index) => (
                    <Grid item xs={6} key={index}>
                        <FormControlLabel
                            control={<Checkbox size="small" checked={item.completed} onChange={() => handleChecklistToggle(index)} sx={{ color: '#7C3AED' }} />}
                            label={<Typography variant="caption" fontWeight="600" color="text.secondary">{item.task}</Typography>}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    const renderTasksSection = () => (
        <Box sx={{ p: 2.5, bgcolor: 'white', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <CheckCircleIcon sx={{ color: '#059669', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight="800" color="text.primary">Clinical Task List</Typography>
            </Stack>
            <Grid container spacing={2}>
                {(formData.taskCategories || []).map((cat) => {
                    const isExpanded = expandedCategories[cat.key] !== false;
                    const completedCount = cat.tasks.filter(t => t.completed).length;
                    const totalCount = cat.tasks.length;
                    const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

                    const toggleCategory = () => {
                        setExpandedCategories(prev => ({
                            ...prev,
                            [cat.key]: !isExpanded
                        }));
                    };

                    return (
                        <Grid item xs={12} md={6} key={cat.key}>
                            <Box sx={{
                                border: '1px solid',
                                borderColor: alpha(cat.color, 0.1),
                                borderRadius: 2,
                                overflow: 'hidden',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                height: '100%'
                            }}>
                                <Box
                                    onClick={toggleCategory}
                                    sx={{
                                        p: 1.5,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        bgcolor: alpha(cat.color, 0.03),
                                        '&:hover': { bgcolor: alpha(cat.color, 0.06) }
                                    }}
                                >
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Box sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: cat.color,
                                            boxShadow: `0 0 8px ${alpha(cat.color, 0.4)}`
                                        }} />
                                        <Typography variant="caption" fontWeight="900" color={cat.color} sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                            {cat.label}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Typography variant="caption" fontWeight="800" sx={{ color: 'text.secondary', opacity: 0.8 }}>
                                            {completedCount}/{totalCount}
                                        </Typography>
                                        <IconButton size="small" sx={{ p: 0.2, color: cat.color }}>
                                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                        </IconButton>
                                    </Stack>
                                </Box>

                                <Collapse in={isExpanded}>
                                    <Box sx={{ p: 2, pt: 0.5 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progress}
                                            sx={{
                                                height: 4,
                                                borderRadius: 2,
                                                mb: 2,
                                                bgcolor: alpha(cat.color, 0.1),
                                                '& .MuiLinearProgress-bar': { bgcolor: cat.color, borderRadius: 2 }
                                            }}
                                        />
                                        <Grid container spacing={1}>
                                            {cat.tasks.map((task) => (
                                                <Grid item xs={6} key={task.id}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={task.completed}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    handleHandoverTaskToggle(cat.key, task.id);
                                                                }}
                                                                sx={{
                                                                    color: cat.color,
                                                                    '&.Mui-checked': { color: cat.color },
                                                                    padding: '4px'
                                                                }}
                                                            />
                                                        }
                                                        label={<Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', userSelect: 'none' }}>{task.label}</Typography>}
                                                        sx={{ ml: 0, mr: 0, width: '100%' }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );

    const renderNoteSection = () => (
        <Box sx={{ p: 2.5, bgcolor: 'white', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <NoteIcon sx={{ color: '#0077B6', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="800" color="text.primary">Handover Note</Typography>
                </Stack>
                <IconButton onClick={handleToggleDictation} sx={{ color: isListening ? 'error.main' : '#0077B6', bgcolor: isListening ? alpha('#F44336', 0.1) : 'transparent' }}>
                    <MicIcon fontSize="small" />
                </IconButton>
            </Stack>
            <TextField
                fullWidth multiline rows={isSidebarExpanded ? 3 : 5}
                placeholder="Enter additional details..."
                value={formData.handoverNote || ''}
                onChange={(e) => setFormData({ ...formData, handoverNote: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' } }}
            />
        </Box>
    );

    // ── Auto-populate SBAR from latest progress note ─────────────────────────

    const autoFetchProgressNotes = useCallback(async (residentId: string) => {
        if (!residentId) return;
        setNotesLoading(true);
        setNotesSource(null);
        try {
            const notes = await clinicalNoteService.getProgressNotesByResident(residentId);
            let list = Array.isArray(notes) ? notes : (notes as any)?.content || [];
            // Sort descending by dateTime or createdAt
            list = [...list].sort((a: any, b: any) => new Date(b.dateTime || b.createdAt || 0).getTime() - new Date(a.dateTime || a.createdAt || 0).getTime());
            const latest = list[0];
            if (latest) {
                const noteText: string = latest.note || latest.content || latest.description || '';
                // Use the latest note to populate SBAR fields intelligently
                setFormData(prev => ({
                    ...prev,
                    situation: noteText.substring(0, 200) || prev.situation,
                    background: latest.background || latest.note?.substring(0, 150) || prev.background,
                    assessment: latest.assessment || latest.clinicalAssessment || prev.assessment,
                    recommendation: latest.recommendation || latest.plan || prev.recommendation,
                }));
                const dateStr = latest.dateTime ? new Date(latest.dateTime).toLocaleString() : 'recent record';
                setNotesSource(`SBAR pre-filled from progress note dated ${dateStr}`);
            }
        } catch {
            // Silently fail
        } finally {
            setNotesLoading(false);
        }
    }, []);

    // Handle resident selection
    const getResidentDisplayName = (item: any): string => {
        if (!item) return 'Unknown';

        const nameFromHandover = item.residentName || item.fullName || item.name;
        if (nameFromHandover && nameFromHandover.toString().trim()) return nameFromHandover.toString().trim();

        const residentId = item.residentId || item.id;
        if (residentId) {
            const matchedResident = residents.find((r: ResidentDTO) => r.id === residentId);
            if (matchedResident) {
                const residentName = matchedResident.fullName || `${matchedResident.firstName || ''} ${matchedResident.middleName ? matchedResident.middleName + ' ' : ''}${matchedResident.lastName || ''}`.trim();
                if (residentName) return residentName;
                return matchedResident.firstName || matchedResident.lastName || matchedResident.id || residentId;
            }
        }

        const first = item.firstName || item?.resident?.firstName || '';
        const middle = item.middleName || item?.resident?.middleName || '';
        const last = item.lastName || item?.resident?.lastName || '';

        const combined = `${first} ${middle ? middle + ' ' : ''}${last}`.trim();
        if (combined) return combined;

        return residentId || 'Unknown';
    };

    const handleResidentChange = async (residentId: string) => {
        setFormData(prev => ({ ...prev, residentId, vitals: { bp: '', temp: '', pulse: '', spO2: '' }, situation: '', background: '', assessment: '', recommendation: '' }));
        setVitalsSource(null);
        setNotesSource(null);
        // Parallel fetch
        autoFetchVitals(residentId);
        autoFetchProgressNotes(residentId);
        fetchActivityTimeline(residentId);
    };


    const handleOpenCreate = () => {
        setFormData(defaultForm());
        setHistoryPage(1);
        setVitalsSource(null);
        setNotesSource(null);
        setIsEditMode(false);
        setSelectedHandover(null);
        setSelectedResident(null);
        setResidentSearchQuery('');
        // Show all residents without filtering out those who already have handovers
        setResidentSearchResults(residents.slice(0, 25));
        setIsDialogOpen(true);
    };

    const handleRowClick = (res: ResidentDTO, ho?: ShiftHandoverDTO) => {
        if (ho) {
            handleOpenEdit(ho);
        } else {
            handleOpenCreate();
            handleSelectResidentFromSearch(res);
        }
    };

    const handleResidentSearchChange = (query: string) => {
        setResidentSearchQuery(query);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

        const getSortedResults = (results: ResidentDTO[]) => {
            return [...results].sort((a, b) => {
                const nameA = (a.fullName || a.firstName || '').toLowerCase();
                const nameB = (b.fullName || b.firstName || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
        };

        if (!query.trim()) {
            setResidentSearchResults(residents.slice(0, 25));
            return;
        }

        searchDebounceRef.current = setTimeout(async () => {
            setIsSearchingResidents(true);
            try {
                const facilityId = localStorage.getItem('currentTenantId') || undefined;
                let results: ResidentDTO[] = [];

                if (facilityId) {
                    results = await residentService.quickSearch(facilityId, query, 'ACTIVE');
                } else {
                    const searchResponse = await residentService.searchResidents(query, 0, 25);
                    if (Array.isArray(searchResponse)) {
                        results = searchResponse;
                    } else {
                        results = (searchResponse?.content || []) as ResidentDTO[];
                    }
                }

                const sortedResults = getSortedResults(Array.isArray(results) ? results : []);
                setResidentSearchResults(sortedResults.slice(0, 25));
            } catch {
                try {
                    const all = await residentService.getAllResidents(0, 100);
                    const allData = Array.isArray(all) ? all : (all as any).content || [];
                    const filtered = allData.filter((r: any) => {
                        const name = `${r.firstName || ''} ${r.middleName || ''} ${r.lastName || ''}`.toLowerCase();
                        return (
                            name.includes(query.toLowerCase()) ||
                            (r.medicalRecordNumber || r.emrNumber || '').toLowerCase().includes(query.toLowerCase()) ||
                            (r.roomNumber || '').toLowerCase().includes(query.toLowerCase())
                        );
                    });
                    const sortedResults = getSortedResults(filtered);
                    setResidentSearchResults(sortedResults.slice(0, 25));
                } catch {
                    setResidentSearchResults([]);
                }
            } finally {
                setIsSearchingResidents(false);
            }
        }, 300);
    };

    const clearResidentSelection = () => {
        setSelectedResident(null);
        setResidentSearchQuery('');
        setResidentSearchResults(residents.slice(0, 25));
        setFormData(prev => ({
            ...prev,
            residentId: '',
            residentName: '',
            roomNumber: '',
            vitals: { bp: '', temp: '', pulse: '', spO2: '' },
            situation: '',
            background: '',
            assessment: '',
            recommendation: '',
        }));
        setVitalsSource(null);
        setNotesSource(null);
    };

    const handleSelectResidentFromSearch = async (resident: ResidentDTO | null) => {
        if (!resident?.id) {
            clearResidentSelection();
            return;
        }

        setSelectedResident(resident);
        setResidentSearchQuery(`${getResidentDisplayName(resident)} ${resident.roomNumber ? `(Room: ${resident.roomNumber}${resident.bedNumber ? `, Bed: ${resident.bedNumber}` : ""})` : ""}`.trim());

        // Call the GET API to check for an existing handover when a resident is selected from the dropdown
        try {
            const existingHandover = await careService.getHandoverByResidentId(resident.id);

            if (existingHandover && existingHandover.id) {
                showSnackbar('Existing handover found. Loading details...', 'success');
                setIsEditMode(true);
                setSelectedHandover(existingHandover);
                setFormData({ ...existingHandover, status: existingHandover.status || 'Stable' });
                setVitalsSource('Loaded from existing handover');
                setNotesSource('Loaded from existing handover');
            } else {
                setIsEditMode(false);
                setSelectedHandover(null);
                setFormData(prev => ({
                    ...defaultForm(),
                    residentId: resident.id,
                    residentName: getResidentDisplayName(resident),
                    roomNumber: resident.roomNumber || '',
                }));

                autoFetchVitals(resident.id);
                autoFetchProgressNotes(resident.id);
            }
        } catch (err) {
            console.error('API Error:', err);
            showSnackbar('Error fetching resident handover details', 'error');
        }

        fetchActivityTimeline(resident.id);
    };


    const handleOpenEdit = async (ho: ShiftHandoverDTO) => {
        setSelectedHandover(ho);
        setFormData({ ...ho, status: ho.status || 'Stable' });
        setHistoryPage(1);
        setVitalsSource(null);
        setNotesSource(null);
        setIsEditMode(true);

        const matchedRes = residents.find(r => r.id === ho.residentId);
        if (matchedRes) {
            setSelectedResident(matchedRes);
            setResidentSearchQuery(`${getResidentDisplayName(matchedRes)} ${matchedRes.roomNumber ? `(Room: ${matchedRes.roomNumber}${matchedRes.bedNumber ? `, Bed: ${matchedRes.bedNumber}` : ""})` : ""}`.trim());

            // Show all residents in the search results
            setResidentSearchResults(residents.slice(0, 25));
        } else {
            const partialRes = { id: ho.residentId, fullName: ho.residentName || 'Unknown', roomNumber: ho.roomNumber || '' } as ResidentDTO;
            setSelectedResident(partialRes);
            setResidentSearchQuery(`${ho.residentName || 'Unknown'} ${ho.roomNumber ? `(Room: ${ho.roomNumber})` : ""}`.trim());
            setResidentSearchResults([partialRes]);
        }

        setIsDialogOpen(true);
        if (ho.residentId) {
            fetchActivityTimeline(ho.residentId);

            // Trigger API only on opening the Edit screen
            const detailedHandover = await careService.getHandoverByResidentId(ho.residentId);
            if (detailedHandover) {
                setSelectedHandover(detailedHandover);
                setFormData({ ...detailedHandover, status: detailedHandover.status || ho.status || 'Stable' });

                const hasVitals = !!(
                    detailedHandover.vitals?.bp ||
                    detailedHandover.vitals?.temp ||
                    detailedHandover.vitals?.pulse ||
                    detailedHandover.vitals?.spO2
                );

                if (!hasVitals) {
                    autoFetchVitals(ho.residentId);
                }
            }
        }
    };


    const handleClose = () => setIsDialogOpen(false);

    const handleCreate = async () => {
        try {
            const res = residents.find(r => r.id === formData.residentId);
            await careService.createHandover({
                ...formData,
                residentName: getResidentDisplayName(res),
                roomNumber: res?.roomNumber || formData.roomNumber || 'N/A',
            });
            showSnackbar('Shift handover recorded successfully!', 'success');
            handleClose();
            fetchData();
        } catch {
            showSnackbar('Failed to record handover', 'error');
        }
    };

    const handleResetAll = () => {
        setSearchTerm('');
        setFilterStatus('');
        setPage(1);
    };

    const handleUpdate = async () => {
        try {
            if (!selectedHandover || !selectedHandover.id) {
                // ── First-time update flow ───────────────────────────────────────
                // Step 1: Call the null-resident endpoint as required by the backend
                await careService.getHandoverByNullResidentId();

                // Step 2: Now create/update with the actual Resident ID
                const res = residents.find(r => r.id === formData.residentId);
                await careService.createHandover({
                    ...formData,
                    residentName: getResidentDisplayName(res),
                    roomNumber: res?.roomNumber || formData.roomNumber || 'N/A',
                });
                showSnackbar('Handover recorded successfully!', 'success');
            } else {
                // ── Subsequent update flow ───────────────────────────────────────
                await careService.updateHandover(selectedHandover.id, formData);
                showSnackbar('Handover updated successfully!', 'success');
            }
            handleClose();
            fetchData();
        } catch {
            showSnackbar('Failed to save handover', 'error');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this handover record?')) return;
        try {
            await careService.deleteHandover(id);
            showSnackbar('Handover deleted', 'success');
            fetchData();
        } catch { showSnackbar('Failed to delete handover', 'error'); }
    };

    const handleHandoverTaskToggle = (catKey: string, taskId: string) => {
        const newCats = [...(formData.taskCategories || [])];
        const cat = newCats.find(c => c.key === catKey);
        if (cat) {
            const t = cat.tasks.find(t => t.id === taskId);
            if (t) {
                t.completed = !t.completed;
                // If it's the mood task, also update the top-level mood field
                if (taskId === 'bd-mood') {
                    setFormData({ ...formData, taskCategories: newCats, mood: t.completed });
                    return;
                }
            }
        }
        setFormData({ ...formData, taskCategories: newCats });
    };

    const handleChecklistToggle = (index: number) => {
        const newChecklist = [...(formData.checklist || [])];
        newChecklist[index].completed = !newChecklist[index].completed;
        setFormData({ ...formData, checklist: newChecklist });
    };

    // Stats & Filtering
    const filteredResidents = residents.filter(res => {
        const ho = handovers.find(h => h.residentId === res.id);

        if (filterStatus) {
            const effectiveStatus = ho?.isCritical ? 'Critical' : (ho?.status || 'Pending');
            if (effectiveStatus.toLowerCase() !== filterStatus.toLowerCase()) return false;
        }

        // 1. Filter by Search Term (Strict Local Filtering)
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            let matchesSearch = false;

            // 1. Check Resident Name
            const residentName = getResidentDisplayName(res).toLowerCase();
            if (residentName.includes(term)) {
                matchesSearch = true;
            }

            // 2. Check Handover Fields
            if (!matchesSearch && ho) {
                matchesSearch = (
                    (ho.situation || '').toLowerCase().includes(term) ||
                    (ho.assessment || '').toLowerCase().includes(term) ||
                    (ho.recommendation || '').toLowerCase().includes(term) ||
                    (ho.handoverNote || '').toLowerCase().includes(term) ||
                    (ho.status || '').toLowerCase().includes(term) ||
                    (ho.summary || '').toLowerCase().includes(term)
                );
            } 
            
            // 3. Fallback for missing handover (matches UI text or 'pending' status)
            if (!matchesSearch && !ho) {
                const noRecordText = 'no handover record for current shift';
                if (noRecordText.includes(term) || 'pending'.includes(term)) {
                    matchesSearch = true;
                }
            }

            if (!matchesSearch) return false;
        }

        // 2. Filter by Category (Tabs)
        if (activeFilter === 'ALL') return true;
        if (activeFilter === 'TODAY') {
            if (!ho) return false;
            const ts = ho.handedOverAt || ho.createdAt;
            return ts && new Date(ts).toDateString() === new Date().toDateString();
        }
        if (activeFilter === 'CRITICAL') {
            return ho?.isCritical === true;
        }
        return true;
    });

    const criticalCount = stats?.criticalAlerts ?? handovers.filter(h => h.isCritical).length;
    const todayCount = stats?.todayHandovers ?? handovers.filter(h => {
        const ts = h.handedOverAt || h.createdAt;
        if (!ts) return false;
        return new Date(ts).toDateString() === new Date().toDateString();
    }).length;

    let avgCompletionStr = stats?.avgTaskCompletion;
    if (!avgCompletionStr) {
        const avg = handovers.length ? Math.round(handovers.reduce((sum, h) => {
            const [done, total] = (h.completionRatio || '0/1').split('/').map(Number);
            const validTotal = total || 1;
            return sum + (done / validTotal);
        }, 0) / handovers.length * 100) : 0;
        avgCompletionStr = `${avg}%`;
    }

    const totalResidentsCount = stats?.totalResidents ?? residents.length;

    const totalPages = Math.ceil(filteredResidents.length / size);
    const displayedResidents = filteredResidents.slice((page - 1) * size, page * size);

    const anyAutoLoading = vitalsLoading || notesLoading;

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", px: 0, pb: 3, pt: 3 }}>
            {/* ── Header ──────────────────────────────────────────────────── */}
            <Box sx={{ mb: 3, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexWrap: { xs: "wrap", sm: "nowrap" }, gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 1, sm: 0 }, flex: 1, minWidth: 0 }}>
                    <Box sx={{ typography: 'h1', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <SwapHorizIcon sx={{ fontSize: 36, color: "#48CAE4" }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" fontWeight="700" sx={{ mb: 0.5, color: '#1A2B45', whiteSpace: { xs: "normal", sm: "nowrap", md: "normal" }, overflow: "hidden", textOverflow: "ellipsis" }}>
                            Shift Handover
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem", whiteSpace: "normal" }}>
                            Standardized SBAR-based care transitions for smooth nursing shift handovers
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    fullWidth={isMobile}
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                    sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 'normal',
                        boxShadow: 'none',
                        bgcolor: '#48CAE4',
                        color: '#000000',
                        '&:hover': { bgcolor: '#3ab3cc' },
                        width: { xs: '100%', sm: 'auto' },
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        ml: { sm: "auto" }
                    }}
                >
                    New Handover
                </Button>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                    { label: 'Total Residents', value: totalResidentsCount, icon: <PeopleAltIcon />, color: '#60a5fa', sub: 'Active in facility', filter: 'ALL' },
                    { label: "Today's Handovers", value: todayCount, icon: <AccessTimeIcon />, color: '#4ade80', sub: `${todayCount}/${totalResidentsCount} Completed`, filter: 'TODAY' },
                    { label: 'Critical Alerts', value: criticalCount, icon: <PriorityHighIcon />, color: '#f87171', sub: criticalCount > 0 ? 'Requires attention' : 'All clear', filter: 'CRITICAL' },
                    { label: 'Avg. Task Completion', value: avgCompletionStr, icon: <AssignmentTurnedInIcon />, color: '#c084fc', sub: 'Across all handovers', filter: 'AVG' },
                ].map((s) => (
                    <Grid item xs={12} sm={6} lg={3} key={s.label}>
                        <StatCard
                            {...s}
                            onClick={s.filter ? () => {
                                setActiveFilter(s.filter as any);
                                setPage(1);
                            } : undefined}
                            active={activeFilter === s.filter}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* ── Search and Filter Bar ── */}
            <Paper sx={{ p: 2, mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }} className="no-print">
                <TextField
                    placeholder="Search by Resident Name, Summary, Note, or Recommendation…"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPage(1);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                {searchLoading ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : searchTerm ? (
                                    <IconButton size="small" onClick={() => { setSearchTerm(''); setPage(1); }}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ) : null}
                            </InputAdornment>
                        ),
                    }}
                    sx={{ flex: 1, minWidth: 250 }}
                />

                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        label="Status"
                        value={filterStatus}
                        onChange={(e) => {
                            setFilterStatus(e.target.value as string);
                            setPage(1);
                        }}
                    >
                        <MenuItem value="">All Status</MenuItem>
                        {statusOptions.map((s: any) => (
                            <MenuItem key={s.valueName || s} value={s.valueName || s}>
                                {s.valueName || s}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Tooltip title="Reset filters">
                    <IconButton onClick={handleResetAll} sx={{ my: 'auto', ml: -1 }}>
                        <RestartAltIcon />
                    </IconButton>
                </Tooltip>
            </Paper>


            {/* ── Table ── */}
            {loading ? (
                <Box display="flex" justifyContent="center" py={10}><CircularProgress sx={{ color: '#0077B6' }} /></Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                        <Table sx={{ minWidth: 750 }}>
                            <TableHead sx={{
                                "& .MuiTableCell-head": {
                                    color: "#434445ff !important",
                                    fontWeight: 700,
                                    fontSize: "13px",
                                    letterSpacing: "0.02em",
                                    py: 1.5
                                }
                            }}>
                                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                                    <TableCell>Resident</TableCell>
                                    <TableCell>SBAR Summary</TableCell>
                                    <TableCell>Recommendation</TableCell>
                                    <TableCell>Note</TableCell>
                                    <TableCell>Completion</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Updated Time</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedResidents.map((res) => {
                                    const ho = handovers.find(h => h.residentId === res.id);
                                    const [done, total] = (ho?.completionRatio || '0/4').split('/').map(Number);
                                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                                    const displayName = getResidentDisplayName(res);
                                    const displayRoomNumber = res.roomNumber || ho?.roomNumber || 'N/A';

                                    return (
                                        <TableRow
                                            key={res.id}
                                            onClick={() => handleRowClick(res, ho)}
                                            sx={{
                                                '&:hover': { bgcolor: alpha('#0077B6', 0.04) },
                                                transition: 'background 0.15s',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <TableCell sx={{ minWidth: 200, width: '25%', verticalAlign: 'top', pt: 2 }}>
                                                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                    <Avatar sx={{ width: 38, height: 38, fontWeight: 800, fontSize: '0.9rem', bgcolor: ho?.isCritical ? alpha('#DC2626', 0.12) : alpha('#0077B6', 0.12), color: ho?.isCritical ? '#DC2626' : '#0077B6' }}>
                                                        {displayName[0]?.toUpperCase() || 'U'}
                                                    </Avatar>
                                                    <Box>
                                                        {displayName}
                                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                                                            <RoomIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                                            Room {displayRoomNumber}
                                                        </Stack>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ width: '25%', minWidth: 220, verticalAlign: 'top', pt: 2 }}>
                                                {ho ? (
                                                    <Box>
                                                        {ho.summary ? (
                                                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.5 }}>{ho.summary}</Typography>
                                                        ) : (
                                                            <>
                                                                <Box component="span" sx={{ color: '#0077B6', fontWeight: 800 }}>S: </Box>{ho.situation}
                                                                <Box sx={{ display: 'block', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: 1.5 }}>
                                                                    <Box component="span" fontWeight={700}>A: </Box>{ho.assessment}
                                                                </Box>
                                                            </>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    "No handover record for current shift"
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ width: '20%', minWidth: 180, verticalAlign: 'top', pt: 2 }}>
                                                {ho ? (
                                                    <Box>
                                                        {ho.recommendation}
                                                        {(ho.safetyAlerts?.length || 0) > 0 && (
                                                            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                                                                {ho.safetyAlerts?.slice(0, 2).map(a => (
                                                                    <Chip key={a} label={a} size="small" icon={<WarningAmberIcon />} color="warning" variant="outlined" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, '& .MuiChip-icon': { fontSize: 14 } }} />
                                                                ))}
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    "—"
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ width: '15%', minWidth: 160, verticalAlign: 'top', pt: 2 }}>
                                                {ho?.handoverNote ? (
                                                    ho.handoverNote
                                                ) : (
                                                    "—"
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 140, verticalAlign: 'top', pt: 2 }}>
                                                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    Tasks
                                                    {done}/{total}
                                                </Box>
                                                <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#0077B6', 0.1), '& .MuiLinearProgress-bar': { bgcolor: pct === 100 ? '#059669' : '#0077B6', borderRadius: 3 } }} />
                                                <Box sx={{ mt: 1, display: 'block', fontWeight: '500' }}>Mood: <Box component="span" fontWeight="700">{ho?.mood ? 'Observed' : '—'}</Box></Box>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 100, verticalAlign: 'top', pt: 2 }}>
                                                {ho ? (() => {
                                                    if (ho.isCritical) {
                                                        const s = getStatusStyle('Critical');
                                                        return <Chip icon={<PriorityHighIcon sx={{ color: `${s.text} !important`, fontSize: '14px !important' }} />} label="Critical" size="small" sx={{ fontWeight: 800, borderRadius: 1.5, fontSize: '0.7rem', bgcolor: s.bg, color: s.text, border: `1px solid ${s.border}` }} />;
                                                    }
                                                    const statusLabel = ho.status || 'Stable';
                                                    const s = getStatusStyle(statusLabel);
                                                    return (
                                                        <Chip
                                                            label={statusLabel}
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 700,
                                                                borderRadius: 1.5,
                                                                fontSize: '0.7rem',
                                                                bgcolor: s.bg,
                                                                color: s.text,
                                                                border: `1px solid ${s.border}`,
                                                            }}
                                                        />
                                                    );
                                                })() : (
                                                    <Chip label="Pending" size="small" sx={{ fontWeight: 700, borderRadius: 1.5, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1' }} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 130, verticalAlign: 'top', pt: 2 }}>
                                                {(() => {
                                                    const dateValue = ho?.updatedAt || ho?.handedOverAt || ho?.createdAt;
                                                    const formatted = dateValue ? formatToUIDateTimeLong(dateValue) : null;
                                                    const isValidFormatted = formatted && formatted !== '—';
                                                    return isValidFormatted ? (
                                                        formatted
                                                    ) : (
                                                        "N/A"
                                                    )
                                                })()}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* ── Pagination ── */}
                    <Box sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="600">
                            Showing {Math.min(filteredResidents.length, (page - 1) * size + 1)} to {Math.min(filteredResidents.length, page * size)} of {filteredResidents.length} residents
                        </Typography>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_e: React.ChangeEvent<unknown>, v: number) => setPage(v)}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{
                                '& .MuiPaginationItem-root': { fontWeight: 700, borderRadius: 1 },
                                '& .Mui-selected': { bgcolor: alpha('#0077B6', 0.1), color: '#0077B6', borderColor: '#0077B6' }
                            }}
                        />
                    </Box>
                </>
            )}

            {!loading && residents.length === 0 && (
                <Paper elevation={0} sx={{ p: 7, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 3, mt: 0 }}>
                    <PeopleAltIcon sx={{ fontSize: 56, color: alpha('#0077B6', 0.3), mb: 1.5 }} />
                    <Typography variant="h6" fontWeight="700" color="text.secondary">No active residents found</Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mb: 2.5 }}>There are currently no active residents in this facility.</Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 'normal',
                            boxShadow: 'none',
                            bgcolor: '#48CAE4',
                            color: '#000000',
                            '&:hover': { bgcolor: '#3ab3cc' },
                            width: { xs: '100%', sm: 'auto' }
                        }}
                    >
                        New Handover
                    </Button>
                </Paper>
            )}

            {/* ─────────────────────────────────────────────────────────────────
               ── Dialog ──
               ───────────────────────────────────────────────────────────────── */}
            <Dialog open={isDialogOpen} onClose={handleClose} maxWidth="lg" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, overflow: 'hidden' } }}>

                {/* Dialog Header */}
                <Box sx={{ bgcolor: '#48CAE4', px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <SwapHorizIcon sx={{ color: '#000000', fontSize: 22 }} />
                            <Typography variant="h6" fontWeight="800" color="#000000">
                                {isEditMode ? 'Edit Handover Record' : 'Perform Shift Handover'}
                            </Typography>
                        </Stack>
                        <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.65)', ml: 4.5, display: 'block', mt: 0.2 }}>
                            Vitals & SBAR are auto-filled from the resident's latest records when you select a resident.
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: '#000000', '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } }}><CloseIcon /></IconButton>
                </Box>

                <DialogContent sx={{ p: 0, bgcolor: '#F8FAFC', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: isMobile ? 'auto' : '85vh' }}>
                    <Grid container sx={{ flexGrow: 1, height: '100%', overflow: 'hidden', position: 'relative' }}>

                        {/* ── MAIN CONTENT AREA ── */}
                        <Grid item xs={12} lg={isSidebarExpanded ? 8.5 : 11.5} sx={{
                            height: '100%',
                            overflowY: 'auto',
                            p: 3,
                            borderRight: { lg: isSidebarExpanded ? '1px solid' : 'none' },
                            borderColor: { lg: 'divider' },
                            transition: 'all 0.3s ease',
                            '&::-webkit-scrollbar': { width: '6px' },
                            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                            '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#0077B6', 0.1), borderRadius: '10px' },
                            '&::-webkit-scrollbar-thumb:hover': { bgcolor: alpha('#0077B6', 0.2) }
                        }}>

                            {/* Resident Selector (Always at top) */}
                            <Box sx={{ mb: 3 }}>
                                <Autocomplete
                                    disabled={isEditMode}
                                    fullWidth
                                    open={isEditMode ? false : residentSearchOpen}
                                    onOpen={() => !isEditMode && setResidentSearchOpen(true)}
                                    onClose={() => setResidentSearchOpen(false)}
                                    options={isEditMode ? [] : residentSearchResults}
                                    getOptionLabel={(option) => option ? (typeof option === 'string' ? option : `${getResidentDisplayName(option)} ${option.roomNumber ? `(Room: ${option.roomNumber}${option.bedNumber ? `, Bed: ${option.bedNumber}` : ""})` : ""}`.trim()) : ''}
                                    value={selectedResident}
                                    inputValue={residentSearchQuery}
                                    onInputChange={(event, value, reason) => reason === 'clear' ? clearResidentSelection() : handleResidentSearchChange(value)}
                                    onChange={(event, value) => {
                                        handleSelectResidentFromSearch(value as ResidentDTO | null);
                                        setResidentSearchOpen(false);
                                    }}
                                    loading={isSearchingResidents}
                                    renderInput={(params) => (
                                        <TextField {...params} required label="Search Resident by Name"
                                            variant="outlined"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {isSearchingResidents ? (
                                                            <CircularProgress color="inherit" size={20} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                )
                                            }}
                                            sx={{
                                                bgcolor: 'white',
                                                '& .MuiFormLabel-asterisk': { color: 'error.main' }
                                            }}
                                        />
                                    )}
                                />
                            </Box>

                            {/* DYNAMIC LAYOUT BASED ON SIDEBAR STATE */}
                            {isSidebarExpanded ? (
                                <Stack spacing={3}>
                                    {renderVitalsSection()}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                        <FormControlLabel
                                            control={<Checkbox checked={!!formData.isCritical} onChange={(e) => setFormData({ ...formData, isCritical: e.target.checked })} color="error" />}
                                            label={<Typography variant="subtitle2" fontWeight="700">Mark as Critical Alert</Typography>}
                                            sx={{ ml: 0 }}
                                        />
                                        {!formData.isCritical && (
                                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                                <InputLabel id="resident-status-label-1" sx={{ fontSize: '0.85rem' }}>Resident Status</InputLabel>
                                                <Select
                                                    labelId="resident-status-label-1"
                                                    label="Resident Status"
                                                    value={formData.status || ''}
                                                    onChange={(e: any) => setFormData({ ...formData, status: e.target.value })}
                                                    sx={{ borderRadius: 2, bgcolor: 'background.paper' }}
                                                >
                                                    {statusOptions.length > 0 ? (
                                                        statusOptions.map((opt: any) => (
                                                            <MenuItem key={opt.id || opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                                                        ))
                                                    ) : (
                                                        ['Stable', 'Needs Attention', 'Sleeping', 'Behavioral Alert', 'Hospitalized'].map((name) => (
                                                            <MenuItem key={name} value={name}>{name}</MenuItem>
                                                        ))
                                                    )}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Box>
                                    {renderSBARForm()}
                                    {renderChecklistSection()}
                                    {renderTasksSection()}
                                    {renderNoteSection()}
                                </Stack>
                            ) : (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Stack spacing={3}>
                                            {renderVitalsSection()}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                                <FormControlLabel
                                                    control={<Checkbox checked={!!formData.isCritical} onChange={(e) => setFormData({ ...formData, isCritical: e.target.checked })} color="error" />}
                                                    label={<Typography variant="subtitle2" fontWeight="700">Mark as Critical Alert</Typography>}
                                                    sx={{ ml: 0 }}
                                                />
                                                {!formData.isCritical && (
                                                    <FormControl size="small" sx={{ minWidth: 160 }}>
                                                        <InputLabel id="resident-status-label-2" sx={{ fontSize: '0.85rem' }}>Resident Status</InputLabel>
                                                        <Select
                                                            labelId="resident-status-label-2"
                                                            label="Resident Status"
                                                            value={formData.status || ''}
                                                            onChange={(e: any) => setFormData({ ...formData, status: e.target.value })}
                                                            sx={{ borderRadius: 2, bgcolor: 'background.paper' }}
                                                        >
                                                            {statusOptions.length > 0 ? (
                                                                statusOptions.map((opt: any) => (
                                                                    <MenuItem key={opt.id || opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                                                                ))
                                                            ) : (
                                                                ['Stable', 'Needs Attention', 'Sleeping', 'Behavioral Alert', 'Hospitalized'].map((name) => (
                                                                    <MenuItem key={name} value={name}>{name}</MenuItem>
                                                                ))
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            </Box>
                                            {renderChecklistSection()}
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Stack spacing={3}>
                                            {renderSBARForm()}
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Stack spacing={3}>
                                            {renderTasksSection()}
                                            {renderNoteSection()}
                                        </Stack>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>

                        {/* RIGHT SIDEBAR */}
                        <Grid item xs={12} lg={isSidebarExpanded ? 3.5 : 0.5} sx={{
                            height: '100%', bgcolor: '#f1f5f9', borderLeft: '1px solid', borderColor: 'divider',
                            display: { xs: 'flex', lg: 'flex' }, flexDirection: 'column', transition: 'all 0.3s ease', overflow: 'hidden'
                        }}>
                            <Box
                                onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 0.5,
                                    minHeight: '38px',
                                    justifyContent: isSidebarExpanded ? 'space-between' : 'center',
                                    flexDirection: isSidebarExpanded ? 'row' : 'column',
                                    cursor: 'pointer',
                                    p: isSidebarExpanded ? 2 : 1,
                                    borderBottom: isSidebarExpanded ? '1px solid' : 'none',
                                    borderColor: 'divider',
                                    bgcolor: 'white'
                                }}
                            >
                                {isSidebarExpanded ? (
                                    <>
                                        <Typography variant="subtitle2" fontWeight="900" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, letterSpacing: 0.5 }}>
                                            <HistoryIcon color="primary" sx={{ fontSize: 20 }} /> RECENT UPDATES
                                        </Typography>
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsSidebarExpanded(!isSidebarExpanded); }}>
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 1, mt: 2 }}>
                                        <Tooltip title="Recent Updates" placement="left">
                                            <TimelineIcon fontSize="small" color="primary" />
                                        </Tooltip>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 800,
                                                writingMode: 'vertical-rl',
                                                transform: 'rotate(180deg)',
                                                color: 'text.primary',
                                                fontSize: '0.7rem',
                                                letterSpacing: '0.1em',
                                                mt: 1,
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            Recent Updates
                                        </Typography>
                                        <IconButton size="small" sx={{ mt: 1 }} onClick={(e) => { e.stopPropagation(); setIsSidebarExpanded(!isSidebarExpanded); }}>
                                            <ChevronLeftIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                            <Collapse in={isSidebarExpanded} timeout="auto" unmountOnExit sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, '& .MuiCollapse-wrapper': { flexGrow: 1, display: 'flex', flexDirection: 'column' }, '& .MuiCollapse-wrapperInner': { flexGrow: 1, display: 'flex', flexDirection: 'column' } }}>
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        minHeight: 0,
                                        overflowY: 'auto',
                                        p: 2,
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: 'rgba(0,0,0,0.2) transparent',
                                        '&::-webkit-scrollbar': {
                                            width: '6px',
                                            display: 'block'
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'rgba(0,0,0,0.02)',
                                            borderRadius: '10px',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: 'rgba(0,0,0,0.2)',
                                            borderRadius: '10px',
                                            '&:hover': {
                                                background: 'rgba(0,0,0,0.3)',
                                            }
                                        }
                                    }}
                                >
                                    {renderActivityTimeline()}
                                </Box>
                            </Collapse>
                        </Grid>
                    </Grid>
                </DialogContent>

                {/* Dialog Footer */}
                <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5, bgcolor: 'white' }}>
                    <Button onClick={handleClose} variant="outlined" color="inherit" sx={{ borderRadius: 2, fontWeight: 700, color: 'text.secondary', borderColor: 'divider' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={anyAutoLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={isEditMode ? handleUpdate : handleCreate}
                        disabled={!formData.residentId || !formData.situation || anyAutoLoading}
                        sx={{ borderRadius: 2, px: 3.5, fontWeight: 'normal', bgcolor: '#48CAE4', color: '#000000', '&:hover': { bgcolor: '#3ab3cc' }, boxShadow: 'none' }}
                    >
                        {isEditMode ? 'Update Record' : 'Complete Handover'}
                    </Button>
                </Box>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2, fontWeight: 700 }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default ShiftHandoverPage;
